import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button, DatePicker, Select, Table, Spin, message, Segmented,
} from "antd";
import {
  ReloadOutlined, FilterOutlined, PrinterOutlined, ArrowLeftOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency } from "../../../utils/formatters";
import { readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { printHtml } from "../../../shared/lib/printHtml";
import {
  DATE_FILTER_PRESET_DEFS,
  getEnabledDateFilterPresets,
  resolveDatePreset,
} from "../../../shared/lib/dateFilterPresets";
import { PageHeader, StatCard, T, cardStyle } from "../../../shared/ui/PageShell";
import {
  buildMetricPages,
  buildSectionDefs,
} from "../lib/accountantDashboardShared";
import { fetchExpenseTypes } from "../../purchasing/lib/expenseTypes";
import usePermissions from "../../../hooks/usePermissions";
import "./AccountantDashboard.css";

const FALLBACK_COMPANY_TYPES = [
  { code: "rent", name: "کرێی عەمار" },
  { code: "utilities", name: "کارەبا و ئاو" },
  { code: "salaries", name: "مووچە" },
  { code: "transport", name: "گواستنەوە" },
  { code: "maintenance", name: "چاککردنەوە" },
  { code: "other", name: "خەرجی دیکە" },
];

const FALLBACK_FARMER_TYPES = [
  { code: "rent", name: "کرێی عەمار" },
  { code: "transport", name: "حمالیە" },
  { code: "moving", name: "گواستنەوە" },
  { code: "watching", name: "پاسەوانی" },
  { code: "other", name: "خەرجی دیکە" },
];

function normalizeTypeList(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((x) => {
      if (typeof x === "string") {
        const code = x.trim();
        return code ? { code, name: code } : null;
      }
      const code = String(x?.code || x?.value || "").trim();
      const name = String(x?.name || x?.label || x?.code || "").trim();
      if (!code && !name) return null;
      return { code: code || name, name: name || code };
    })
    .filter(Boolean);
}

/** Prefer catalogue from API; never mix company list with farmer list. */
function pickTypeCatalogue(primary, fromApiFilters, fallback) {
  const fromPrimary = normalizeTypeList(primary);
  if (fromPrimary.length) return fromPrimary;
  const fromFilters = normalizeTypeList(fromApiFilters);
  if (fromFilters.length) return fromFilters;
  return normalizeTypeList(fallback);
}

function useExpenseTypeOptions() {
  const [companyTypes, setCompanyTypes] = useState([]);
  const [farmerTypes, setFarmerTypes] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [companyRes, farmerRows] = await Promise.all([
          apiClient.get("/company-expense-types").catch(() => null),
          fetchExpenseTypes({ force: true }).catch(() => []),
        ]);
        if (cancelled) return;
        const companyRaw = companyRes?.data?.types
          ?? companyRes?.data?.data
          ?? (Array.isArray(companyRes?.data) ? companyRes.data : []);
        const companyActive = Array.isArray(companyRaw)
          ? companyRaw.filter((r) => r?.is_active !== false)
          : [];
        // Strict separation — never assign farmer rows to companyTypes
        setCompanyTypes(normalizeTypeList(companyActive));
        setFarmerTypes(normalizeTypeList(farmerRows));
      } catch {
        if (!cancelled) {
          setCompanyTypes([]);
          setFarmerTypes([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { companyTypes, farmerTypes };
}

/** Apply expense-type filters to metrics/sections when needed (also works if API ignores type params). */
function applyExpenseTypeFilters(data, companyExpenseType, farmerExpenseType) {
  if (!data) return data;
  const next = {
    ...data,
    metrics: { ...(data.metrics || {}) },
    sections: { ...(data.sections || {}) },
  };

  if (companyExpenseType) {
    const raw = data.sections?.company_expenses?.rows || [];
    const canFilter = raw.some((r) => r.type_code || r.type);
    if (canFilter) {
      const rows = raw.filter((r) => {
        const code = String(r.type_code || "").trim();
        const label = String(r.type || "").trim();
        return code === companyExpenseType || label === companyExpenseType;
      });
      const total = round2(rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0));
      next.sections.company_expenses = {
        ...(data.sections?.company_expenses || {}),
        rows,
        stats: { count: rows.length, total },
      };
      next.metrics.company_expenses = total;
      next.metrics.warehouse_expenses = total;
    }
  }

  if (farmerExpenseType) {
    const raw = data.sections?.farmer_charges?.rows || [];
    const canFilter = raw.some((r) => r.type_code || r.type || r.type_name);
    if (canFilter) {
      const rows = raw.filter((r) => {
        const code = String(r.type_code || "").trim();
        const label = String(r.type_name || r.type || "").trim();
        return code === farmerExpenseType || label === farmerExpenseType;
      });
      const total = round2(rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0));
      next.sections.farmer_charges = {
        ...(data.sections?.farmer_charges || {}),
        rows,
        stats: { count: rows.length, total },
      };
      next.metrics.farmer_charges = total;
    }
  }

  return next;
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function useAccountantFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const presetFromUrl = searchParams.get("preset") || "month";
  const fromUrl = searchParams.get("from");
  const toUrl = searchParams.get("to");
  const userFromUrl = searchParams.get("user");
  const vendorFromUrl = searchParams.get("vendor");
  const companyTypeFromUrl = searchParams.get("cet");
  const farmerTypeFromUrl = searchParams.get("fet");

  const [preset, setPreset] = useState(presetFromUrl);
  const [range, setRange] = useState(() => {
    if (fromUrl || toUrl) {
      return [
        fromUrl ? dayjs(fromUrl) : null,
        toUrl ? dayjs(toUrl) : null,
      ];
    }
    return [dayjs().startOf("month"), dayjs()];
  });
  const [userId, setUserId] = useState(userFromUrl ? Number(userFromUrl) : null);
  const [vendorId, setVendorId] = useState(vendorFromUrl ? Number(vendorFromUrl) : null);
  const [companyExpenseType, setCompanyExpenseType] = useState(companyTypeFromUrl || null);
  const [farmerExpenseType, setFarmerExpenseType] = useState(farmerTypeFromUrl || null);

  const syncUrl = useCallback((next) => {
    const p = new URLSearchParams();
    if (next.preset) p.set("preset", next.preset);
    if (next.range?.[0]) p.set("from", dayjs(next.range[0]).format("YYYY-MM-DD HH:mm:ss"));
    if (next.range?.[1]) p.set("to", dayjs(next.range[1]).format("YYYY-MM-DD HH:mm:ss"));
    if (next.userId) p.set("user", String(next.userId));
    if (next.vendorId) p.set("vendor", String(next.vendorId));
    if (next.companyExpenseType) p.set("cet", String(next.companyExpenseType));
    if (next.farmerExpenseType) p.set("fet", String(next.farmerExpenseType));
    setSearchParams(p, { replace: true });
  }, [setSearchParams]);

  const applyPreset = (key) => {
    setPreset(key);
    let nextRange = range;
    if (key !== "custom") {
      nextRange = resolveDatePreset(key === "month" ? "this_month" : key, { withTime: true });
    }
    setRange(nextRange);
    syncUrl({
      preset: key, range: nextRange, userId, vendorId, companyExpenseType, farmerExpenseType,
    });
  };

  const resetFilters = () => {
    const today = dayjs();
    const nextRange = [today.startOf("month"), today];
    setUserId(null);
    setVendorId(null);
    setCompanyExpenseType(null);
    setFarmerExpenseType(null);
    setPreset("month");
    setRange(nextRange);
    syncUrl({
      preset: "month", range: nextRange, userId: null, vendorId: null,
      companyExpenseType: null, farmerExpenseType: null,
    });
  };

  const filterQuery = useMemo(() => {
    const q = new URLSearchParams();
    if (preset) q.set("preset", preset);
    if (range?.[0]) q.set("from", dayjs(range[0]).format("YYYY-MM-DD HH:mm:ss"));
    if (range?.[1]) q.set("to", dayjs(range[1]).format("YYYY-MM-DD HH:mm:ss"));
    if (userId) q.set("user", String(userId));
    if (vendorId) q.set("vendor", String(vendorId));
    if (companyExpenseType) q.set("cet", String(companyExpenseType));
    if (farmerExpenseType) q.set("fet", String(farmerExpenseType));
    const s = q.toString();
    return s ? `?${s}` : "";
  }, [preset, range, userId, vendorId, companyExpenseType, farmerExpenseType]);

  return {
    preset,
    setPreset,
    range,
    setRange,
    userId,
    setUserId,
    vendorId,
    setVendorId,
    companyExpenseType,
    setCompanyExpenseType,
    farmerExpenseType,
    setFarmerExpenseType,
    applyPreset,
    resetFilters,
    syncUrl,
    filterQuery,
  };
}

function useAccountantData(range, userId, vendorId, companyExpenseType, farmerExpenseType, t) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (range?.[0]) params.date_from = dayjs(range[0]).format("YYYY-MM-DD HH:mm:ss");
      if (range?.[1]) params.date_to = dayjs(range[1]).format("YYYY-MM-DD HH:mm:ss");
      if (userId) params.user_id = userId;
      if (vendorId) params.vendor_id = vendorId;
      if (companyExpenseType) params.company_expense_type = companyExpenseType;
      if (farmerExpenseType) params.farmer_expense_type = farmerExpenseType;
      const res = await apiClient.get("/reports/accountant-dashboard", { params });
      setData(res.data);
    } catch (e) {
      message.error(e?.response?.data?.message || t("loadError", "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, [range, userId, vendorId, companyExpenseType, farmerExpenseType, t]);

  useEffect(() => { load(); }, [load]);

  return { data, loading, load };
}

/**
 * Live accountant-dashboard farmer_charges rows often omit `type`.
 * Load typed rows from /vendor-expenses so the table + type filter work
 * against older production APIs too.
 */
function useFarmerChargesTable({ enabled, range, vendorId, typeFilter, farmerTypeOptions }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const labels = useMemo(() => {
    const map = {};
    (farmerTypeOptions || []).forEach((x) => {
      map[x.code] = x.name || x.code;
      if (x.name) map[x.name] = x.name;
    });
    FALLBACK_FARMER_TYPES.forEach((x) => {
      if (!map[x.code]) map[x.code] = x.name;
    });
    return map;
  }, [farmerTypeOptions]);

  const load = useCallback(async () => {
    if (!enabled) {
      setRows(null);
      return;
    }
    setLoading(true);
    try {
      const params = { include_archived: 1 };
      if (range?.[0]) params.start_date = dayjs(range[0]).format("YYYY-MM-DD HH:mm:ss");
      if (range?.[1]) params.end_date = dayjs(range[1]).format("YYYY-MM-DD HH:mm:ss");
      if (vendorId) params.vendor_id = vendorId;
      if (typeFilter) params.type = typeFilter;
      const res = await apiClient.get("/vendor-expenses", { params });
      const list = Array.isArray(res.data?.expenses) ? res.data.expenses : [];
      const mapped = list
        .filter((e) => !e.cancelled_at)
        .map((e) => {
          const code = String(e.type || "").trim();
          const label = labels[code] || code || "—";
          return {
            id: e.id,
            date: e.created_at,
            type: label,
            type_code: code,
            type_name: label,
            farmer: e.vendor_name,
            amount: round2(e.amount),
            user: e.created_by_name,
          };
        });
      setRows(mapped);
    } catch {
      // Fall back to dashboard section rows (may lack type on old API)
      setRows(null);
    } finally {
      setLoading(false);
    }
  }, [enabled, range, vendorId, typeFilter, labels]);

  useEffect(() => { load(); }, [load]);

  const total = useMemo(
    () => round2((rows || []).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)),
    [rows]
  );

  return { rows, total, loading, load };
}

function AccFilters({
  t, preset, applyPreset, range, setRange, setPreset, userId, setUserId,
  vendorId, setVendorId, companyExpenseType, setCompanyExpenseType,
  farmerExpenseType, setFarmerExpenseType,
  filterUsers, filterVendors, filterCompanyExpenseTypes, filterFarmerExpenseTypes,
  load, loading, resetFilters, syncUrl,
  showVendorFilter = true,
  showCompanyExpenseType = true,
  showFarmerExpenseType = true,
}) {
  const companyOpts = useMemo(
    () => normalizeTypeList(filterCompanyExpenseTypes).map((x) => ({ value: x.code, label: x.name || x.code })),
    [filterCompanyExpenseTypes]
  );
  const farmerOpts = useMemo(
    () => normalizeTypeList(filterFarmerExpenseTypes).map((x) => ({ value: x.code, label: x.name || x.code })),
    [filterFarmerExpenseTypes]
  );

  const urlState = () => ({
    preset, range, userId, vendorId, companyExpenseType, farmerExpenseType,
  });

  return (
    <div className="acc-filter-card" style={{ ...cardStyle, marginBottom: 18, padding: "14px 16px" }}>
      <div className="acc-filter-row">
        <div className="acc-filter-field acc-filter-field--grow">
          <div className="acc-filter-label">{t("period", "Period")}</div>
          <div className="acc-segmented-wrap">
            <Segmented
              value={preset}
              onChange={applyPreset}
              options={[
                ...getEnabledDateFilterPresets(readCachedSystemConfig())
                  .map((key) => {
                    const def = DATE_FILTER_PRESET_DEFS.find((d) => d.key === key);
                    if (!def) return null;
                    return {
                      value: key === "this_month" ? "month" : key,
                      label: t(def.labelKey, def.defaultLabel),
                    };
                  })
                  .filter(Boolean),
                { value: "custom", label: t("custom", "Custom") },
              ]}
            />
          </div>
        </div>
        <div className="acc-filter-field">
          <div className="acc-filter-label">{t("dateRange", "Date range")}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.muted }}>{t("dateRangeStart", "Start")}</span>
              <DatePicker
                value={range?.[0] || null}
                showTime={{ format: "HH:mm" }}
                format="YYYY-MM-DD HH:mm"
                needConfirm={false}
                onChange={(d) => {
                  const next = [d || null, range?.[1] || null];
                  setPreset("custom");
                  setRange(next);
                  syncUrl({ ...urlState(), preset: "custom", range: next });
                }}
                style={{ borderRadius: 10, width: "100%" }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.muted }}>{t("dateRangeEnd", "End")}</span>
              <DatePicker
                value={range?.[1] || null}
                showTime={{ format: "HH:mm" }}
                format="YYYY-MM-DD HH:mm"
                needConfirm={false}
                onChange={(d) => {
                  const next = [range?.[0] || null, d || null];
                  setPreset("custom");
                  setRange(next);
                  syncUrl({ ...urlState(), preset: "custom", range: next });
                }}
                style={{ borderRadius: 10, width: "100%" }}
              />
            </div>
          </div>
        </div>
        <div className="acc-filter-field">
          <div className="acc-filter-label">{t("cashier", "Cashier")}</div>
          <Select
            allowClear showSearch optionFilterProp="label"
            placeholder={t("accAllUsers", "All users")}
            style={{ width: "100%" }}
            value={userId}
            onChange={(v) => {
              const next = v ?? null;
              setUserId(next);
              syncUrl({ ...urlState(), userId: next });
            }}
            options={filterUsers.map((u) => ({ value: u.id, label: u.name }))}
          />
        </div>
        {showVendorFilter && (
          <div className="acc-filter-field">
            <div className="acc-filter-label">{t("vendorsColName", "Farmer")}</div>
            <Select
              allowClear showSearch optionFilterProp="label"
              placeholder={t("accAllFarmers", "All farmers")}
              style={{ width: "100%" }}
              value={vendorId}
              onChange={(v) => {
                const next = v ?? null;
                setVendorId(next);
                syncUrl({ ...urlState(), vendorId: next });
              }}
              options={filterVendors.map((v) => ({ value: v.id, label: v.name }))}
            />
          </div>
        )}
        {showCompanyExpenseType && (
          <div className="acc-filter-field">
            <div className="acc-filter-label">{t("accCompanyExpenseType", "Company expense type")}</div>
            <Select
              allowClear showSearch optionFilterProp="label"
              placeholder={t("accAllExpenseTypes", "All types")}
              style={{ width: "100%" }}
              value={companyExpenseType}
              onChange={(v) => {
                const next = v ?? null;
                setCompanyExpenseType(next);
                syncUrl({ ...urlState(), companyExpenseType: next });
              }}
              options={companyOpts}
              notFoundContent={t("accNoExpenseTypes", "No expense types found")}
            />
          </div>
        )}
        {showFarmerExpenseType && (
          <div className="acc-filter-field">
            <div className="acc-filter-label">{t("accFarmerExpenseType", "Farmer expense type")}</div>
            <Select
              allowClear showSearch optionFilterProp="label"
              placeholder={t("accAllExpenseTypes", "All types")}
              style={{ width: "100%" }}
              value={farmerExpenseType}
              onChange={(v) => {
                const next = v ?? null;
                setFarmerExpenseType(next);
                syncUrl({ ...urlState(), farmerExpenseType: next });
              }}
              options={farmerOpts}
              notFoundContent={t("accNoExpenseTypes", "No expense types found")}
            />
          </div>
        )}
        <div className="acc-filter-actions">
          <Button type="primary" icon={<FilterOutlined />} onClick={load} loading={loading} style={{ borderRadius: 10 }}>
            {t("accApplyFilters", "Apply")}
          </Button>
          <Button onClick={resetFilters} style={{ borderRadius: 10 }}>
            {t("reset", "Reset")}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Overview: filter + full KPI cards → each opens its own page. */
function AccountantOverview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const filters = useAccountantFilters();
  const { companyTypes, farmerTypes } = useExpenseTypeOptions();
  const { data: rawData, loading, load } = useAccountantData(
    filters.range, filters.userId, filters.vendorId,
    filters.companyExpenseType, filters.farmerExpenseType, t
  );
  const data = useMemo(
    () => applyExpenseTypeFilters(rawData, filters.companyExpenseType, filters.farmerExpenseType),
    [rawData, filters.companyExpenseType, filters.farmerExpenseType]
  );

  const mainCode = data?.main_currency_code || readCachedSystemConfig()?.currency_code || "IQD";
  const m = data?.metrics || {};
  const filterUsers = data?.filters?.users || [];
  const filterVendors = data?.filters?.vendors || [];
  const filterCompanyExpenseTypes = useMemo(
    () => pickTypeCatalogue(companyTypes, data?.filters?.company_expense_types, FALLBACK_COMPANY_TYPES),
    [companyTypes, data?.filters?.company_expense_types]
  );
  const filterFarmerExpenseTypes = useMemo(
    () => pickTypeCatalogue(farmerTypes, data?.filters?.farmer_expense_types, FALLBACK_FARMER_TYPES),
    [farmerTypes, data?.filters?.farmer_expense_types]
  );
  const cashOnHand = m.cash_on_hand ?? 0;
  const money = useCallback((v) => formatCurrency(v ?? 0, mainCode), [mainCode]);

  const metrics = useMemo(() => buildMetricPages(t), [t]);
  const configuredExpenseType = m.configured_farmer_expense_type
    || readCachedSystemConfig()?.accountant_farmer_expense_type
    || null;
  const configuredExpenseLabel = useMemo(() => {
    const custom = (m.configured_farmer_expense_label
      || readCachedSystemConfig()?.accountant_farmer_expense_label
      || "").trim();
    if (custom) return custom;
    if (!configuredExpenseType) {
      return t("accConfiguredFarmerExpenseUnset", "Farmer Expense (set in Settings)");
    }
    const hit = filterFarmerExpenseTypes.find((x) => x.code === configuredExpenseType);
    return hit?.name || configuredExpenseType;
  }, [configuredExpenseType, filterFarmerExpenseTypes, t, m.configured_farmer_expense_label]);

  // Cash on hand is scoped by the cashier (user) filter server-side — his sales
  // in, minus his farmer payments/expenses/refunds out. It is NOT scoped by the
  // farmer filter (cash in the drawer isn't attributable to one farmer), so it
  // stays locked only when a farmer filter is active without a cashier filter.
  const scopedByFarmerOnly = Boolean(filters.vendorId) && !filters.userId;

  const cards = useMemo(() => Object.entries(metrics)
    .filter(([key]) => key !== "audit")
    .map(([key, cfg]) => {
      const locked = key === "cash" && scopedByFarmerOnly;
      return {
        key,
        icon: cfg.icon,
        iconBg: cfg.iconBg,
        label: cfg.settingsExpenseType ? configuredExpenseLabel : cfg.label,
        value: cfg.getValue(m, money, t),
        color: cfg.getColor?.(m),
        locked,
        lockedHint: locked
          ? t("accCashOnHandLockedHint", "Shop-wide total — not scoped by farmer filter")
          : undefined,
      };
    }), [metrics, m, money, t, configuredExpenseLabel, scopedByFarmerOnly]);

  const printReport = () => {
    if (!data) return;
    const esc = (x) => String(x ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const rangeLabel = filters.range[0] || filters.range[1]
      ? `${filters.range[0] ? dayjs(filters.range[0]).format("YYYY-MM-DD") : "…"} → ${filters.range[1] ? dayjs(filters.range[1]).format("YYYY-MM-DD") : "…"}`
      : t("finAllTime", "All time");
    const company = (readCachedSystemConfig()?.invoice_company_name || readCachedSystemConfig()?.site_name || "").trim();
    const row = (l, v, c = "#111") =>
      `<div style="display:flex;justify-content:space-between;gap:16px;padding:5px 0;border-bottom:1px solid #f1f5f9"><span style="color:#64748b">${esc(l)}</span><b style="color:${c}">${esc(v)}</b></div>`;

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(t("accountantDashboardTitle", "Accountant Dashboard"))}</title>
      <style>body{font-family:system-ui,sans-serif;padding:24px;color:#0f172a}h1{margin:0 0 8px}.meta{color:#64748b;margin-bottom:20px}</style></head><body>
      <h1>${esc(company || t("accountantDashboardTitle", "Accountant Dashboard"))}</h1>
      <div class="meta">${esc(rangeLabel)}</div>
      ${row(t("accTotalSales", "Total Sales"), money(m.total_sales))}
      ${row(t("accCashOnHand", "Cash on Hand"), money(cashOnHand), cashOnHand >= 0 ? "#16a34a" : "#e11d48")}
      ${row(t("accTaxes", "Taxes"), money(m.taxes), "#2563eb")}
      ${row(t("accCompanyExpenses", "Warehouse Expenses"), money(m.warehouse_expenses ?? m.company_expenses ?? 0), "#ea580c")}
      ${row(t("accCustomerDebts", "Customer Debts"), money(m.customer_debts))}
      ${row(t("accFarmerOutstanding", "Farmer Outstanding"), money(m.farmer_outstanding), "#e11d48")}
      ${row(t("accNetProfit", "Net Profit"), money(m.net_profit))}
      ${row(t("accReturns", "Returns"), money(m.returns), "#e11d48")}
      ${row(t("accCashRefunds", "Cash Refunds"), money(m.cash_refunds), "#e11d48")}
      ${row(t("accCancellations", "Cancelled Sales"), money(m.cancellations), "#b45309")}
      ${row(t("accSoldWeight", "Total Items Sold by Weight"), `${Number(m.sold_weight_kg || 0).toLocaleString("en-US")} ${esc(t("kg", "KG"))}`)}
      ${row(t("accCogs", "Cost of Goods Sold"), money(m.cogs), "#e11d48")}
      </body></html>`;

    printHtml(html);
  };

  return (
    <div className="acc-dash" style={{ padding: "clamp(12px, 2.5vw, 24px)", background: T.bg, minHeight: "100%" }}>
      <PageHeader
        breadcrumb={[t("sidebarSectionFinances", "Finances"), t("accountantDashboardTitle", "Accountant Dashboard")]}
        title={t("accountantDashboardTitle", "Accountant Dashboard")}
        subtitle={t("accountantDashboardHint", "Click a card to open its full report page")}
        action={
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ borderRadius: 10 }}>
              {t("currencyTotalsRefresh", "Refresh")}
            </Button>
            <Button type="primary" icon={<PrinterOutlined />} onClick={printReport} disabled={!data} style={{ borderRadius: 10 }}>
              {t("common.print", { defaultValue: "Print" })}
            </Button>
          </div>
        }
      />

      <AccFilters
        t={t}
        {...filters}
        filterUsers={filterUsers}
        filterVendors={filterVendors}
        filterCompanyExpenseTypes={filterCompanyExpenseTypes}
        filterFarmerExpenseTypes={filterFarmerExpenseTypes}
        load={load}
        loading={loading}
        showCompanyExpenseType={false}
        showFarmerExpenseType={false}
      />

      <Spin spinning={loading}>
        <div className="acc-group-title">{t("accGroupDetails", "Overview")}</div>
        <div className="acc-stat-grid acc-stat-grid--full">
          {cards.map((c) => (
            <StatCard
              key={c.key}
              icon={c.icon}
              iconBg={c.iconBg}
              label={c.label}
              value={c.value}
              valueColor={c.color}
              locked={c.locked}
              lockedHint={c.lockedHint}
              onClick={c.locked ? undefined : () => navigate(`/reports/accountant/${c.key}${filters.filterQuery}`)}
              className="acc-stat-card--full"
            />
          ))}
        </div>
      </Spin>
    </div>
  );
}

/** Full page for one KPI card. */
function AccountantMetricPage() {
  const { t } = useTranslation();
  const { metric: metricParam } = useParams();
  const location = useLocation();
  const metric = location.pathname === "/audit" || location.pathname.startsWith("/audit/")
    ? "audit"
    : metricParam;
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const filters = useAccountantFilters();
  const { companyTypes, farmerTypes } = useExpenseTypeOptions();
  const metrics = useMemo(() => buildMetricPages(t), [t]);
  const page = metrics[metric];

  // System Audit requires its own permission; other metrics need reports.view.
  useEffect(() => {
    if (metric === "audit" && !hasPermission("reports.audit")) {
      navigate(`/reports/accountant${filters.filterQuery}`, { replace: true });
      return;
    }
    if (metric && metric !== "audit" && !hasPermission("reports.view")) {
      if (hasPermission("reports.audit")) {
        navigate(`/audit${filters.filterQuery}`, { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }
  }, [metric, hasPermission, navigate, filters.filterQuery]);

  const isCompanyExpenses = page?.section === "company_expenses";
  const isFarmerCharges = page?.section === "farmer_charges";
  const isConfiguredFarmerExpense = Boolean(page?.settingsExpenseType);
  // Type for the settings card is locked — only System Settings may change it.
  const settingsExpenseType = readCachedSystemConfig()?.accountant_farmer_expense_type || null;
  // Company report: only company type (no farmer filters). Farmer report: only farmer type (no company type).
  const activeCompanyType = isFarmerCharges ? null : (isCompanyExpenses ? filters.companyExpenseType : null);
  const activeFarmerType = isConfiguredFarmerExpense
    ? settingsExpenseType
    : (isCompanyExpenses ? null : (isFarmerCharges ? filters.farmerExpenseType : null));
  const activeVendorId = isCompanyExpenses ? null : filters.vendorId;

  const { data: rawData, loading: dashLoading, load: loadDash } = useAccountantData(
    filters.range, filters.userId, activeVendorId,
    activeCompanyType, activeFarmerType, t
  );
  const data = useMemo(
    () => applyExpenseTypeFilters(rawData, activeCompanyType, activeFarmerType),
    [rawData, activeCompanyType, activeFarmerType]
  );

  useEffect(() => {
    if (!page) navigate(`/reports/accountant${filters.filterQuery}`, { replace: true });
  }, [page, navigate, filters.filterQuery]);

  const mainCode = data?.main_currency_code || readCachedSystemConfig()?.currency_code || "IQD";
  const m = data?.metrics || {};
  const sections = data?.sections || {};
  const filterUsers = data?.filters?.users || [];
  const filterVendors = data?.filters?.vendors || [];
  const filterCompanyExpenseTypes = useMemo(
    () => pickTypeCatalogue(companyTypes, data?.filters?.company_expense_types, FALLBACK_COMPANY_TYPES),
    [companyTypes, data?.filters?.company_expense_types]
  );
  const filterFarmerExpenseTypes = useMemo(
    () => pickTypeCatalogue(farmerTypes, data?.filters?.farmer_expense_types, FALLBACK_FARMER_TYPES),
    [farmerTypes, data?.filters?.farmer_expense_types]
  );

  const {
    rows: farmerChargeRows,
    total: farmerChargeTotal,
    loading: farmerChargesLoading,
    load: loadFarmerCharges,
  } = useFarmerChargesTable({
    enabled: Boolean(isFarmerCharges),
    range: filters.range,
    vendorId: activeVendorId,
    typeFilter: activeFarmerType,
    farmerTypeOptions: filterFarmerExpenseTypes,
  });

  const money = useCallback((v) => formatCurrency(v ?? 0, mainCode), [mainCode]);

  const expenseTypeLabels = useMemo(() => {
    const company = {};
    const farmer = {};
    filterCompanyExpenseTypes.forEach((x) => {
      company[x.code] = x.name || x.code;
      if (x.name) company[x.name] = x.name;
    });
    filterFarmerExpenseTypes.forEach((x) => {
      farmer[x.code] = x.name || x.code;
      if (x.name) farmer[x.name] = x.name;
    });
    return { company, farmer };
  }, [filterCompanyExpenseTypes, filterFarmerExpenseTypes]);

  // Prefer /vendor-expenses rows (has type). Else map dashboard rows if they include type.
  const sectionsForTable = useMemo(() => {
    const s = sections || {};
    const mapRows = (rows, labels) => (rows || []).map((r) => {
      const code = String(r?.type_code || "").trim();
      const raw = String(r?.type_name || r?.type || "").trim();
      const label = labels[code] || labels[raw] || raw || code || "—";
      return { ...r, type: label, type_name: label, type_code: code || raw };
    });
    const farmerRows = farmerChargeRows != null
      ? farmerChargeRows
      : mapRows(s.farmer_charges?.rows, expenseTypeLabels.farmer);
    const farmerTotal = farmerChargeRows != null
      ? farmerChargeTotal
      : (s.farmer_charges?.stats?.total ?? 0);
    return {
      ...s,
      company_expenses: {
        ...(s.company_expenses || {}),
        rows: mapRows(s.company_expenses?.rows, expenseTypeLabels.company),
      },
      farmer_charges: {
        ...(s.farmer_charges || {}),
        rows: farmerRows,
        stats: {
          ...(s.farmer_charges?.stats || {}),
          count: farmerRows.length,
          total: farmerTotal,
        },
      },
    };
  }, [sections, expenseTypeLabels, farmerChargeRows, farmerChargeTotal]);

  const sectionDefs = useMemo(
    () => buildSectionDefs(sectionsForTable, money, t, expenseTypeLabels),
    [sectionsForTable, money, t, expenseTypeLabels]
  );
  const sec = sectionDefs.find((s) => s.key === page?.section);

  const loading = dashLoading || farmerChargesLoading;
  const load = useCallback(() => {
    loadDash();
    if (isFarmerCharges) loadFarmerCharges();
  }, [loadDash, loadFarmerCharges, isFarmerCharges]);

  const back = () => {
    if (hasPermission("reports.view")) {
      navigate(`/reports/accountant${filters.filterQuery}`);
      return;
    }
    navigate("/");
  };

  if (!page) return null;

  const pageTitle = isConfiguredFarmerExpense
    ? ((readCachedSystemConfig()?.accountant_farmer_expense_label
      || m.configured_farmer_expense_label
      || "").trim()
      || filterFarmerExpenseTypes.find((x) => x.code === settingsExpenseType)?.name
      || settingsExpenseType
      || t("accConfiguredFarmerExpenseUnset", "Farmer Expense (set in Settings)"))
    : page.label;

  const metricsForValue = isFarmerCharges && farmerChargeRows != null
    ? (isConfiguredFarmerExpense
      ? { ...m, configured_farmer_expense: farmerChargeTotal, farmer_charges: farmerChargeTotal }
      : { ...m, farmer_charges: farmerChargeTotal })
    : m;
  const value = page.getValue(metricsForValue, money, t);
  const valueColor = page.getColor?.(metricsForValue);
  const subValue = typeof page.getSubValue === "function"
    ? page.getSubValue(metricsForValue, money, t)
    : null;
  const heroHint = metric === "audit"
    ? t("accAuditHeroHint", "Track what staff did: sales created/edited, payments, cancels, returns, and product/farmer changes")
    : null;

  return (
    <div className="acc-dash" style={{ padding: "clamp(12px, 2.5vw, 24px)", background: T.bg, minHeight: "100%" }}>
      <PageHeader
        breadcrumb={[
          t("sidebarSectionFinances", "Finances"),
          { title: <a onClick={back} style={{ cursor: "pointer" }}>{t("accountantDashboardTitle", "Accountant Dashboard")}</a> },
          pageTitle,
        ]}
        title={pageTitle}
        subtitle={
          isConfiguredFarmerExpense
            ? t("accConfiguredFarmerExpenseDetailHint", "Expense type is fixed in System Settings. Date / user / farmer filters still apply.")
            : (sec?.title || t("accMetricDetailHint", "Full detail for this metric"))
        }
        action={
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button icon={<ArrowLeftOutlined />} onClick={back} style={{ borderRadius: 10 }}>
              {t("back", "Back")}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ borderRadius: 10 }}>
              {t("currencyTotalsRefresh", "Refresh")}
            </Button>
          </div>
        }
      />

      <AccFilters
        t={t}
        {...filters}
        vendorId={activeVendorId}
        companyExpenseType={activeCompanyType}
        farmerExpenseType={activeFarmerType}
        filterUsers={filterUsers}
        filterVendors={filterVendors}
        filterCompanyExpenseTypes={filterCompanyExpenseTypes}
        filterFarmerExpenseTypes={filterFarmerExpenseTypes}
        load={load}
        loading={loading}
        showVendorFilter={!isCompanyExpenses}
        showCompanyExpenseType={isCompanyExpenses}
        showFarmerExpenseType={isFarmerCharges && !isConfiguredFarmerExpense}
      />

      <Spin spinning={loading}>
        <div className="acc-metric-hero" style={{ ...cardStyle, marginBottom: 18, padding: "22px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 16,
                background: page.iconBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 28,
                flexShrink: 0,
              }}
            >
              {page.icon}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                {metric === "audit" ? t("accOrderChanges", "Changes") : page.label}
              </div>
              <div style={{ fontSize: "clamp(1.6rem, 4vw, 2.2rem)", fontWeight: 800, color: valueColor || T.text, lineHeight: 1.2, marginTop: 4 }}>
                {value}
              </div>
              {heroHint && (
                <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>{heroHint}</div>
              )}
              {subValue && (
                <div style={{ fontSize: 13, color: T.sub, marginTop: 6, fontWeight: 600 }}>{subValue}</div>
              )}
            </div>
            {sec?.chips && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {sec.chips}
              </div>
            )}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.borderL}`, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: T.text, display: "inline-flex", alignItems: "center", gap: 8 }}>
              {sec?.icon} {sec?.title || page.label}
            </span>
          </div>
          <Table
            size="middle"
            rowKey={(r, i) => r.id ?? `${metric}-${i}`}
            columns={sec?.columns || []}
            dataSource={sec?.rows || []}
            pagination={(sec?.rows?.length || 0) > 40 ? { pageSize: 40 } : false}
            scroll={{ x: true }}
            locale={{ emptyText: sec?.empty || t("noDataFound", "No data") }}
          />
        </div>
      </Spin>
    </div>
  );
}

const AccountantDashboard = () => {
  const { metric } = useParams();
  const location = useLocation();
  if (metric || location.pathname === "/audit" || location.pathname.startsWith("/audit/")) {
    return <AccountantMetricPage />;
  }
  return <AccountantOverview />;
};

export default AccountantDashboard;
